import { useEffect, useRef } from "react";
import { ShortcutDescriptor } from "../Constant/shortcuts";

export interface ShortcutOptions {
  preventDefault?: boolean;
  stopPropagation?: boolean;
  stopImmediatePropagation?: boolean;
  enabled?: boolean | (() => boolean);
  enableOnFormTags?: boolean;
  enableOnContentEditable?: boolean;
  capture?: boolean;
  ignoreRepeat?: boolean;
}

export interface ShortcutBinding {
  shortcut: ShortcutDescriptor;
  handler: (e: KeyboardEvent) => void;
  options?: ShortcutOptions;
}

const DEFAULT_OPTIONS: Required<
  Pick<
    ShortcutOptions,
    | "preventDefault"
    | "stopPropagation"
    | "stopImmediatePropagation"
    | "enabled"
    | "enableOnFormTags"
    | "enableOnContentEditable"
    | "capture"
    | "ignoreRepeat"
  >
> = {
  preventDefault: true,
  stopPropagation: true,
  stopImmediatePropagation: false,
  enabled: true,
  enableOnFormTags: true,
  enableOnContentEditable: true,
  capture: false,
  ignoreRepeat: false,
};

const MODIFIER_ALIASES: Record<string, "ctrl" | "shift" | "alt" | "meta"> = {
  ctrl: "ctrl",
  control: "ctrl",
  shift: "shift",
  alt: "alt",
  option: "alt",
  meta: "meta",
  command: "meta",
  cmd: "meta",
};

const KEY_ALIASES: Record<string, string> = {
  arrowup: "up",
  arrowdown: "down",
  arrowleft: "left",
  arrowright: "right",
  escape: "esc",
  esc: "esc",
  return: "enter",
  " ": "space",
  spacebar: "space",
  delete: "delete",
  del: "delete",
  backspace: "backspace",
  "?": "/",
};

const normalizeKey = (key: string) => {
  const lowerKey = key.trim().toLowerCase();
  return KEY_ALIASES[lowerKey] || lowerKey;
};

const parseShortcutKey = (shortcutKey: string) => {
  return shortcutKey.split("+").map(normalizeKey).filter(Boolean);
};

const isFormTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;

  const tagName = target.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    target.getAttribute("role") === "textbox" ||
    target.getAttribute("role") === "spinbutton"
  );
};

const isContentEditableTarget = (target: EventTarget | null) => {
  return target instanceof HTMLElement && target.isContentEditable;
};

const hasMatchingModifiers = (
  e: KeyboardEvent,
  requiredModifiers: Set<"ctrl" | "shift" | "alt" | "meta">,
) => {
  return (
    requiredModifiers.has("ctrl") === e.ctrlKey &&
    requiredModifiers.has("shift") === e.shiftKey &&
    requiredModifiers.has("alt") === e.altKey &&
    requiredModifiers.has("meta") === e.metaKey
  );
};

const isShortcutMatch = (e: KeyboardEvent, shortcutKey: string) => {
  const keys = parseShortcutKey(shortcutKey);
  const requiredModifiers = new Set<"ctrl" | "shift" | "alt" | "meta">();
  const actionKeys: string[] = [];

  keys.forEach((key) => {
    const modifier = MODIFIER_ALIASES[key];
    if (modifier) {
      requiredModifiers.add(modifier);
      return;
    }

    actionKeys.push(key);
  });

  if (!hasMatchingModifiers(e, requiredModifiers)) return false;

  const pressedKey = normalizeKey(e.key);
  if (actionKeys.length === 0) {
    return Boolean(MODIFIER_ALIASES[pressedKey]);
  }

  return actionKeys.includes(pressedKey);
};

const getOptions = (
  commonOptions: ShortcutOptions,
  bindingOptions?: ShortcutOptions,
) => ({
  ...DEFAULT_OPTIONS,
  ...commonOptions,
  ...bindingOptions,
});

export const useKeyboardShortcuts = (
  bindings: ShortcutBinding[],
  commonOptions: ShortcutOptions = {},
) => {
  const bindingsRef = useRef(bindings);
  const commonOptionsRef = useRef(commonOptions);
  const useCapture = bindings.some(
    (binding) => getOptions(commonOptions, binding.options).capture,
  );

  useEffect(() => {
    bindingsRef.current = bindings;
    commonOptionsRef.current = commonOptions;
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      bindingsRef.current.some((binding) => {
        const options = getOptions(commonOptionsRef.current, binding.options);

        if (!options.enabled) return false;
        if (options.ignoreRepeat && e.repeat) return false;
        if (!options.enableOnFormTags && isFormTarget(e.target)) return false;
        if (
          !options.enableOnContentEditable &&
          isContentEditableTarget(e.target)
        ) {
          return false;
        }
        if (!isShortcutMatch(e, binding.shortcut.key)) return false;

        if (options.preventDefault) {
          e.preventDefault();
        }
        if (options.stopPropagation) {
          e.stopPropagation();
        }
        if (options.stopImmediatePropagation) {
          e.stopImmediatePropagation();
        }

        binding.handler(e);
        return true;
      });
    };

    window.addEventListener("keydown", handleKeyDown, useCapture);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, useCapture);
    };
  }, [useCapture]);
};

export const useKeyboardShortcut = (
  shortcut: ShortcutDescriptor,
  callback: (e: KeyboardEvent) => void,
  options: ShortcutOptions = { preventDefault: true, stopPropagation: true },
) => {
  useKeyboardShortcuts([{ shortcut, handler: callback, options }]);
};
